import { getUnique } from "@datassential/platform-ui-lib";

export function getThousandAlias (n: number): string {
    let result;
    if (n >= 1000000) {
        result = `${+(n / 1000000).toFixed(1)}M `;
    }
    else if (n >= 1000) {
        result = `${+(n / 1000).toFixed(1)}K `;
    }
    else {
        result = `${n}`;
    }
    return result;
}


export function getFormattedThousand(number: number = 0, separator: string = ','): string {
    if (number < 0) return `-${getFormattedThousand(-number)}`;
    let numberString = Math.trunc(number).toString();
    let result = "";
    while (numberString.length > 3) {
        result = `${separator}${numberString.substr(numberString.length - 3)}${result}`;
        numberString = numberString.substr(0, numberString.length - 3);
    }

    result = `${numberString}${result}`;
    return result;
}

export function getDecimal(n: number): number {
    const int = parseInt(`${n}`);
    return n - int;
}

export function getDecimalAfterPoint (n: number, decimalSize: number = 2): string {
    const decimal = getDecimal(n);
    return decimal.toFixed(decimalSize).split('.')[1];
}

/**
 * Narrows a raw API numeric to a finite number, or null when it carries no usable
 * value. Non-finite floats (NaN, +/-Infinity) arrive from the backend either as
 * numbers or as their string spellings, and absent values as null/undefined/''.
 * Returning null lets callers render an explicit 'N/A' instead of coercing the
 * value to a plausible-looking 0.
 */
export function toFiniteOrNull(value: number | string | null | undefined): number | null {
    if (value === null || value === undefined || value === '') {
        return null;
    }

    const numeric = Number(value);

    return Number.isFinite(numeric) ? numeric : null;
}

export function getFormattedThousandWithDecimal(n: number, decimalSize: number = 2): string {
    // Nullish is treated as 0 (callers rely on it), but a non-finite value is a real
    // data problem and must stay visible rather than be formatted as '0.0'.
    const value = n ?? 0;

    if (!Number.isFinite(value)) {
        return 'N/A';
    }

    const factor = 10 ** decimalSize;
    const rounded = Math.round((value + Number.EPSILON) * factor) / factor;
    const [integerPart, decimalPart] = rounded.toFixed(decimalSize).split('.');
    return `${getFormattedThousand(Number(integerPart))}.${decimalPart}`;
}

export function getAverage (arr: number[]) {
    let sum = 0;
    for (let i = 0; i < arr.length; i++) {
        sum += arr[i];
    }

    return sum / arr.length;
}

export function getFormattedPrice(value: number): string {
    const hasDecimals = value - Math.floor(value) > 0;
    return hasDecimals ? value.toFixed(2) : `${value}`;
}

export const getFilenameFromContentDisposition = (disposition: string | null): string => {
    let filename = "";
    if (disposition && disposition.indexOf('attachment') !== -1) {
        const filenameRegex = new RegExp(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
        const matches = disposition.match(filenameRegex);
        if (matches != null && matches[1]) filename = matches[1].replace(/['"]/g, '');
    }

    return filename;
}

export function downloadFile(url: string, filename?: string): void {
    const link = document.createElement('a');

    if (typeof link.download === 'undefined') {
        window.location.href = url;
    } else {
        link.href = url;
        link.download = filename || "true";
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    }
}

type NavigatorIncludesMS = Navigator & { msSaveBlob?: (blob: Blob, filename?: string) => void };

export function downloadBlobFile(blob: Blob, filename?: string) {
    const navigator = window.navigator as NavigatorIncludesMS;

    if (navigator.msSaveBlob && typeof navigator.msSaveBlob !== 'undefined') {
        navigator.msSaveBlob(blob, filename);
    } else {
        let URL = window.URL || window.webkitURL;
        const downloadUrl = URL.createObjectURL(blob);

        if (filename) {
            downloadFile(downloadUrl, filename);
        } else {
            window.location.href = downloadUrl;
        }

        setTimeout(() => {
            URL.revokeObjectURL(downloadUrl);
        }, 100);
    }
}

export function deepCopy<A>(a: A): A {
    return JSON.parse(JSON.stringify(a));
}

export function deepEqual<A, B>(a: A, b: B): boolean {
    return JSON.stringify(a) === JSON.stringify(b);
}

export function prepareForSorting(value: any): string | number {
    switch (typeof value) {
        case "string": return value.toLowerCase();
        case "boolean": return value ? 1 : 0;
        default: return value;
    }
}

export function highlightMatches(str: string, searchQuery: string): string {
    const searchRegExp = new RegExp(`(${escapeRegExp(searchQuery.trim())})`, "gi");
    if (searchQuery.trim() !== "") {
        str = str.replace(searchRegExp, "<b>$1</b>");
    }
    return str;
}

export function minusArrayUnique(a1: any[], a2: any[]) {
    return a1.filter(i => !a2.includes(i));
}

export function uniteArraysUnique(a1: any[], a2: any[]) {
    return Array.from(new Set([...a1, ...a2]));
}

export function isInRange(val: number, leftEdge: number, rightEdge: number): boolean {
    return val >= leftEdge && val <= rightEdge;
}

export function escapeRegExp(string: string): string {
    return string.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'); // $& means the whole matched string
}

export function parseWordsFromString(query: string): string[] {
    if (!query) return [];

    return getUnique(
        query
            .split('\n')
            .flatMap(line => line.split('\t'))
            .flatMap(line => line.split(','))
            .map(str => str.trim().toLowerCase())
            .filter(Boolean)
    );
}
